import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { Address } from 'viem';

import { DeserializedUser, LaunchpadSalePoolType, WalletInterface } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import {
  DEFAULT_TARGET_CHAIN,
  LAUNCHPAD_DEPOSIT_CONTRACT,
  LAUNCHPAD_PRO_RATA_CONTRACT,
  launchpadToken,
  tokensInfo,
} from '@shared/constants';
import { weiToNumber } from '@shared/helpers/web3/formatters/numberToWei';
import { getToken } from '@shared/helpers/web3/getToken';

import { isProRataType } from '@launchpad/utils/contract';

import useDepositTokenQuery from './useDepositTokenQuery';

interface UserDepositedInfo {
  baseDeposited: bigint;
  boosted: boolean;
  baseAllocation: bigint;
  baseDeposit: bigint;
  baseRefund: bigint;
  baseRefunded: bigint;
  boostDeposit: bigint;
  boostRefund: bigint;
  boostRefunded: bigint;
  nonce: number;
  boostAverageExchangeRate: bigint;
  share: bigint;
  baseDepositDecimals: number;
  boostDepositDecimals: number;
  baseToken: string;
  boostToken: string;
}

const getUserDeposit = async ({
  type,
  userEvmWallet,
  user,
  depositContractAddress,
  depositTokenDecimals = launchpadToken.decimals,
  depositTokenSymbol = launchpadToken.symbol,
}: {
  type?: LaunchpadSalePoolType;
  userEvmWallet: WalletInterface | undefined;
  user: DeserializedUser | undefined | null;
  depositContractAddress: string | null | undefined;
  depositTokenDecimals?: number;
  depositTokenSymbol?: string;
}): Promise<UserDepositedInfo | null> => {
  if (!user || !depositContractAddress) {
    return null;
  }

  let result: UserDepositedInfo = {
    baseDeposited: BigInt(0),
    boosted: false,
    baseAllocation: BigInt(0),
    baseDeposit: BigInt(0),
    baseRefund: BigInt(0),
    baseRefunded: BigInt(0),
    boostDeposit: BigInt(0),
    boostRefund: BigInt(0),
    boostRefunded: BigInt(0),
    nonce: 0,
    boostAverageExchangeRate: BigInt(0),
    share: BigInt(0),
    baseDepositDecimals: depositTokenDecimals,
    boostDepositDecimals: tokensInfo.SQR.decimals,
    baseToken: depositTokenSymbol,
    boostToken: tokensInfo.SQR.symbol,
  };

  if (isProRataType(type)) {
    const [proRataInfo, baseTokenAddress, boostTokenAddress] = await Promise.all([
      await readContract(wagmiConfig, {
        ...LAUNCHPAD_PRO_RATA_CONTRACT,
        chainId: DEFAULT_TARGET_CHAIN.id,
        address: depositContractAddress as Address,
        functionName: 'fetchAccountInfo',
        args: [userEvmWallet?.attributes?.address || '0x000'],
      }),
      await readContract(wagmiConfig, {
        ...LAUNCHPAD_PRO_RATA_CONTRACT,
        chainId: DEFAULT_TARGET_CHAIN.id,
        address: depositContractAddress as Address,
        functionName: 'baseToken',
      }),
      await readContract(wagmiConfig, {
        ...LAUNCHPAD_PRO_RATA_CONTRACT,
        chainId: DEFAULT_TARGET_CHAIN.id,
        address: depositContractAddress as Address,
        functionName: 'boostToken',
      }),
    ]);

    const baseToken =
      (await getToken({
        tokenAddress: baseTokenAddress,
      })) || tokensInfo.USDT;
    const boostToken =
      (await getToken({
        tokenAddress: boostTokenAddress,
      })) || tokensInfo.SQR;

    if (proRataInfo) {
      result = {
        ...proRataInfo,
        baseToken: baseToken.symbol || 'N/A',
        boostToken: boostToken.symbol || 'N/A',
        baseDepositDecimals: baseToken.decimals,
        boostDepositDecimals: boostToken.decimals,
      };
    }
  } else {
    const regularInfo = await readContract(wagmiConfig, {
      ...LAUNCHPAD_DEPOSIT_CONTRACT,
      chainId: DEFAULT_TARGET_CHAIN.id,
      address: depositContractAddress as Address,
      functionName: 'fetchUserFundItem',
      args: [String(user?.id) || ''],
    });

    result.baseDeposited = regularInfo.depositedAmount;
    result.baseAllocation = regularInfo.depositedAmount;
  }

  return new Promise((resolve) => {
    resolve(result);
  });
};

const useSelfDepositedAmountQuery = ({
  depositContractAddress,
  type,
}: {
  depositContractAddress?: string | null;
  type?: LaunchpadSalePoolType;
}) => {
  const { userEvmWallet, user } = useAuth();
  const { data: depositTokenInfo } = useDepositTokenQuery({
    depositContractAddress,
    type,
  });

  return useQuery({
    queryFn: () =>
      getUserDeposit({
        type,
        userEvmWallet,
        user,
        depositContractAddress,
        depositTokenDecimals: depositTokenInfo?.decimals,
        depositTokenSymbol: depositTokenInfo?.symbol,
      }),
    select: (data) => {
      return {
        ...data,
        baseDeposited: weiToNumber(data?.baseDeposited || 0, data?.baseDepositDecimals),
        baseAllocation: weiToNumber(data?.baseAllocation || 0, data?.baseDepositDecimals),
        baseDeposit: weiToNumber(data?.baseDeposit || 0, data?.baseDepositDecimals),
        baseRefund: weiToNumber(data?.baseRefund || 0, data?.baseDepositDecimals),
        baseRefunded: weiToNumber(data?.baseRefunded || 0, data?.baseDepositDecimals),
        boostDeposit: weiToNumber(data?.boostDeposit || 0, data?.boostDepositDecimals),
        boostRefund: weiToNumber(data?.boostRefund || 0, data?.boostDepositDecimals),
        boostRefunded: weiToNumber(data?.boostRefunded || 0, data?.boostDepositDecimals),
        boostAverageExchangeRate: weiToNumber(
          data?.boostAverageExchangeRate || 0,
          data?.boostDepositDecimals,
        ),
        share: weiToNumber(data?.share || 0, data?.boostDepositDecimals),
      };
    },
    queryKey: [
      'selfDepositedAmount',
      user?.id,
      depositTokenInfo?.decimals,
      depositContractAddress,
      type,
    ],
    staleTime: 0,
    enabled: !!depositContractAddress && !!user?.id && !!depositTokenInfo,
  });
};

export const useRefetchAllSelfDepositedAmountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.refetchQueries({
        queryKey: ['selfDepositedAmount'],
      }),
  });
};

export default useSelfDepositedAmountQuery;
